/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from '@web-stories-wp/react';
import { __, sprintf } from '@web-stories-wp/i18n';
import {
  Button,
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
  Icons,
  Input,
  NumericInput,
  Text,
  THEME_CONSTANTS,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { MULTIPLE_DISPLAY_VALUE, MULTIPLE_VALUE } from '../../../../constants';
import {
  setLottieParameterValue,
  unsetLottieParameterValue,
} from '../../../../elements/lottie/contract';
import ColorInput from '../../../form/color/colorInput';
import Row from '../../../form/row';
import Tooltip from '../../../tooltip';
import { SimplePanel } from '../../panel';
import { getCommonValue, inputContainerStyleOverride } from '../../shared';
import {
  getCommonLottieParameters,
  getEffectiveParameterValue,
  getParameterLabel,
  hasParameterOverride,
  lottieColorValueToPattern,
  patternToHexColor,
} from './utils';
import { getLottieTextRowsConfig } from '../../../../elements/lottie/textRows';
import MultiLineCopyInput from './multiLineCopyInput';

const ParameterRow = styled(Row)`
  align-items: stretch;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  min-width: 0;
`;

const Control = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  width: 100%;
`;

const TextControl = styled(Input)`
  flex: 1 1 auto;
  min-width: 0;
`;

const NumberControl = styled(NumericInput)`
  flex: 1 1 auto;
  min-width: 0;
`;

const ColorControl = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const ResetButton = styled(Button)`
  flex: 0 0 auto;
`;

const getInputValue = (value) => (value === MULTIPLE_VALUE ? '' : value ?? '');

const getPlaceholder = (value) =>
  value === MULTIPLE_VALUE ? MULTIPLE_DISPLAY_VALUE : '';

function TextParameterInput({ label, maxLength, value, onSubmit }) {
  const inputValue = getInputValue(value);
  const isMixed = value === MULTIPLE_VALUE;
  const [currentValue, setCurrentValue] = useState(inputValue);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setCurrentValue(inputValue);
    setIsDirty(false);
  }, [inputValue]);

  const submitCurrentValue = useCallback(() => {
    if (!isDirty) {
      return;
    }

    onSubmit(currentValue);
    setIsDirty(false);
  }, [currentValue, isDirty, onSubmit]);

  return (
    <TextControl
      aria-label={label}
      value={currentValue}
      maxLength={maxLength}
      onChange={(evt) => {
        setCurrentValue(evt.target.value);
        setIsDirty(true);
      }}
      onBlur={submitCurrentValue}
      onKeyDown={(evt) => {
        if (evt.key === 'Enter') {
          evt.currentTarget.blur();
        }
      }}
      placeholder={getPlaceholder(value)}
      isIndeterminate={isMixed && !isDirty}
      containerStyleOverride={inputContainerStyleOverride}
    />
  );
}

TextParameterInput.propTypes = {
  label: PropTypes.string.isRequired,
  maxLength: PropTypes.number,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([MULTIPLE_VALUE]),
  ]),
  onSubmit: PropTypes.func.isRequired,
};

function LottieParametersPanel({ selectedElements, pushUpdate }) {
  const wrappedElements = selectedElements.filter(getLottieTextRowsConfig);
  const wrappedElement =
    selectedElements.length === 1 ? wrappedElements[0] : null;
  const wrapConfig = wrappedElement
    ? getLottieTextRowsConfig(wrappedElement)
    : null;
  const parameters = useMemo(
    () => getCommonLottieParameters(selectedElements),
    [selectedElements]
  );

  const updateParameter = useCallback(
    (key, value) => {
      pushUpdate((element) => {
        if (value === '') {
          return {
            parameterValues: unsetLottieParameterValue(
              element.parameterValues,
              key
            ),
          };
        }

        return {
          parameterValues: setLottieParameterValue(
            element.parameterSchema,
            element.parameterValues,
            key,
            value
          ),
        };
      }, true);
    },
    [pushUpdate]
  );

  const resetParameter = useCallback(
    (key) => {
      pushUpdate(
        (element) => ({
          parameterValues: unsetLottieParameterValue(
            element.parameterValues,
            key
          ),
        }),
        true
      );
    },
    [pushUpdate]
  );

  if (!parameters.length) {
    return null;
  }

  return (
    <SimplePanel
      name="lottieParameters"
      title={__('Parameters', 'web-stories')}
    >
      {wrappedElement && (
        <ParameterRow>
          <Text size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
            {wrapConfig.label}
          </Text>
          <MultiLineCopyInput
            key={wrappedElement.id}
            element={wrappedElement}
            pushUpdate={pushUpdate}
          />
        </ParameterRow>
      )}
      {wrappedElements.length > 0 && !wrappedElement && (
        <Text size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
          {__(
            'Select one animation to edit its multi-line copy.',
            'web-stories'
          )}
        </Text>
      )}
      {parameters
        .filter(
          (parameter) =>
            !wrappedElements.some(
              (element) =>
                getLottieTextRowsConfig(element).parameterKey === parameter.key
            )
        )
        .map((parameter) => {
          const label = getParameterLabel(parameter);
          const value = getCommonValue(selectedElements, (element) =>
            getEffectiveParameterValue(element, parameter.key)
          );
          const hasOverride = selectedElements.some((element) =>
            hasParameterOverride(element, parameter.key)
          );
          const resetLabel = sprintf(
            /* translators: %s: Lottie parameter label. */
            __('Reset %s', 'web-stories'),
            label
          );

          return (
            <ParameterRow key={parameter.key}>
              <FieldLabel>
                <Text
                  as="span"
                  size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}
                >
                  {label}
                </Text>
              </FieldLabel>
              <Control>
                {parameter.type === 'text' && (
                  <TextParameterInput
                    label={label}
                    maxLength={parameter.constraints?.maxLength}
                    value={value}
                    onSubmit={(nextValue) =>
                      updateParameter(parameter.key, nextValue)
                    }
                  />
                )}
                {parameter.type === 'number' && (
                  <NumberControl
                    aria-label={label}
                    value={getInputValue(value)}
                    allowEmpty
                    isFloat
                    min={parameter.constraints?.min}
                    max={parameter.constraints?.max}
                    onChange={(_evt, nextValue) => {
                      if (value === MULTIPLE_VALUE && nextValue === '') {
                        return;
                      }

                      updateParameter(parameter.key, nextValue);
                    }}
                    onBlur={(_evt, nextValue) => {
                      if (value === MULTIPLE_VALUE && nextValue === '') {
                        return;
                      }

                      updateParameter(parameter.key, nextValue);
                    }}
                    placeholder={getPlaceholder(value)}
                    isIndeterminate={value === MULTIPLE_VALUE}
                    containerStyleOverride={inputContainerStyleOverride}
                  />
                )}
                {parameter.type === 'color' && (
                  <ColorControl>
                    <ColorInput
                      label={label}
                      value={lottieColorValueToPattern(value)}
                      onChange={(pattern) => {
                        const nextValue = patternToHexColor(pattern);
                        if (nextValue) {
                          updateParameter(parameter.key, nextValue);
                        }
                      }}
                      allowsGradient={false}
                      allowsOpacity={false}
                    />
                  </ColorControl>
                )}
                {hasOverride && (
                  <Tooltip title={resetLabel}>
                    <ResetButton
                      aria-label={resetLabel}
                      type={BUTTON_TYPES.TERTIARY}
                      size={BUTTON_SIZES.SMALL}
                      variant={BUTTON_VARIANTS.SQUARE}
                      onClick={() => resetParameter(parameter.key)}
                    >
                      <Icons.Cross />
                    </ResetButton>
                  </Tooltip>
                )}
              </Control>
            </ParameterRow>
          );
        })}
    </SimplePanel>
  );
}

LottieParametersPanel.propTypes = {
  selectedElements: PropTypes.array.isRequired,
  pushUpdate: PropTypes.func.isRequired,
};

export default LottieParametersPanel;
